/**
 * useNotesList Hook
 *
 * Business logic for notes list view including:
 * - Fetching notes with pagination via v2 RTK Query endpoint
 * - Search functionality with debouncing
 * - Filter by status (all, reviewing, completed)
 * - Sort order (newest/oldest)
 * - Date range filtering
 * - Note title only search toggle
 * - Batch selection for delete operations
 * - Event tracking
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNoteSigningEnabled, useUserState } from '@features/user';
import { useEventTracking } from '@services/statsig';
import type { NoteCompletionStatus } from '@store/generated/notes';
import type { Dayjs } from 'dayjs';

import { useDebounce } from '../../../hooks/useDebounce';
import type { UserNoteTypeII } from '../../../types/types';
import { getNoteRoute } from '../../../utils/noteRouting';
import { useDeleteNoteMutation, useGetNotesListQuery } from '../store/api';
import { convertNoteListItem } from '../utils/convertNoteListItem';
import { getDeletableNoteIds } from '../utils/noteSelection';

export type NoteCategory = 'all' | 'reviewing' | 'completed';
export type SortOrder = -1 | 1; // -1 = newest first, 1 = oldest first

export interface UseNotesListOptions {
  /** Initial items per page */
  initialPageSize?: number;
  /** Group ID for group notes list (undefined for user notes) */
  groupId?: string;
  /** Enable date range filtering */
  enableDateFilter?: boolean;
}

export interface UseNotesListReturn {
  // Data
  notes: UserNoteTypeII[];
  isLoading: boolean;
  isLoadingFresh: boolean;
  error: boolean;
  totalCount: number;

  // Pagination
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (size: number) => void;

  // Filters
  category: NoteCategory;
  sortOrder: SortOrder;
  searchTerm: string;
  noteTitleOnly: boolean;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  setCategory: (category: NoteCategory) => void;
  setSortOrder: (order: SortOrder) => void;
  setSearchTerm: (term: string) => void;
  setNoteTitleOnly: (titleOnly: boolean) => void;
  setStartDate: (date: Dayjs | null) => void;
  setEndDate: (date: Dayjs | null) => void;

  // Selection
  selectedNoteIds: string[];
  isAllSelected: boolean;
  toggleNoteSelection: (noteId: string) => void;
  selectAllNotes: () => void;
  clearSelection: () => void;

  // Actions
  deleteSelectedNotes: () => Promise<{ failedCount: number }>;
  navigateToNote: (note: UserNoteTypeII) => void;
  refresh: () => void;

  // State helpers
  isEmpty: boolean;
  isSearchActive: boolean;
  hasFirstNote: boolean;

  // Feature flags
  hasNoteSigning: boolean;
}

export function useNotesList(options: UseNotesListOptions = {}): UseNotesListReturn {
  const { initialPageSize = 20, groupId, enableDateFilter = true } = options;

  const userState = useUserState();
  const navigate = useNavigate();
  const { note: noteMetrics } = useEventTracking();

  const hasNoteSigning = useNoteSigningEnabled();

  // Pagination state
  const [currentPage, setCurrentPageState] = useState(1);
  const [itemsPerPage, setItemsPerPageState] = useState<number>(() => {
    const saved = localStorage.getItem('noteItemsPerPage');
    return saved ? parseInt(saved) : initialPageSize;
  });

  // Filter state
  const [category, setCategoryState] = useState<NoteCategory>(() => {
    const saved = localStorage.getItem('noteFilterSelection');
    return (saved as NoteCategory) || 'all';
  });

  const [sortOrder, setSortOrderState] = useState<SortOrder>(() => {
    const saved = localStorage.getItem('noteSortOrder');
    return saved ? (parseInt(saved) as SortOrder) : -1;
  });

  const [searchTerm, setSearchTermState] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [noteTitleOnly, setNoteTitleOnlyState] = useState<boolean>(() => {
    const saved = localStorage.getItem('noteTitleOnly');
    return saved ? (JSON.parse(saved) as boolean) : false;
  });

  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  // Selection state
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);

  // Effective group ID (for group notes list)
  const effectiveGroupId = groupId ?? userState?.group_ids?.[0];

  // Skip the query when group notes are requested but no group ID is available
  const skipQuery = groupId !== undefined && !effectiveGroupId;

  // Build RTK Query args
  const queryArgs = useMemo(
    () => ({
      page: currentPage,
      perPage: itemsPerPage,
      organizationId: groupId !== undefined ? effectiveGroupId : undefined,
      completionStatus:
        category === 'all'
          ? undefined
          : category === 'completed'
            ? ('completed,signed' as NoteCompletionStatus)
            : (category as NoteCompletionStatus),
      search: debouncedSearchTerm || undefined,
      noteTitleOnly: noteTitleOnly || undefined,
      startDate:
        enableDateFilter && startDate
          ? (startDate.valueOf() / 1000).toString()
          : undefined,
      endDate:
        enableDateFilter && endDate
          ? (endDate.valueOf() / 1000).toString()
          : undefined,
      sortOrder,
    }),
    [
      currentPage,
      itemsPerPage,
      effectiveGroupId,
      groupId,
      category,
      debouncedSearchTerm,
      noteTitleOnly,
      enableDateFilter,
      startDate,
      endDate,
      sortOrder,
    ],
  );

  const {
    data,
    isLoading: rtkIsLoading,
    isFetching,
    error: rtkError,
    refetch,
  } = useGetNotesListQuery(queryArgs, { skip: skipQuery });

  // Derive data from RTK Query response
  const notes = useMemo(
    () => (data?.items ?? []).map(convertNoteListItem),
    [data?.items],
  );
  const totalCount = data?.total ?? 0;
  const isLoading = skipQuery ? false : rtkIsLoading;
  const isLoadingFresh = isFetching && !rtkIsLoading;
  const error = !!rtkError;

  // Delete mutation
  const [deleteNote] = useDeleteNoteMutation();

  // Pagination handlers
  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page);
  }, []);

  const setItemsPerPage = useCallback((size: number) => {
    localStorage.setItem('noteItemsPerPage', size.toString());
    setItemsPerPageState(size);
    setCurrentPageState(1);
  }, []);

  // Filter handlers
  const setCategory = useCallback((newCategory: NoteCategory) => {
    localStorage.setItem('noteFilterSelection', newCategory);
    setCategoryState(newCategory);
    setCurrentPageState(1);
    setSelectedNoteIds([]);
  }, []);

  const setSortOrder = useCallback((newOrder: SortOrder) => {
    localStorage.setItem('noteSortOrder', newOrder.toString());
    setSortOrderState(newOrder);
  }, []);

  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
  }, []);

  const setNoteTitleOnly = useCallback((titleOnly: boolean) => {
    localStorage.setItem('noteTitleOnly', JSON.stringify(titleOnly));
    setNoteTitleOnlyState(titleOnly);
  }, []);

  // Selection handlers
  const toggleNoteSelection = useCallback((noteId: string) => {
    setSelectedNoteIds(prev =>
      prev.includes(noteId) ? prev.filter(id => id !== noteId) : [...prev, noteId],
    );
  }, []);

  // Signed notes can't be deleted, and selection only exists to feed the
  // bulk delete — select-all and isAllSelected must both derive from this
  // same set or the Select All toggle desyncs on pages with signed notes.
  const deletableNoteIds = useMemo(() => getDeletableNoteIds(notes), [notes]);

  const selectAllNotes = useCallback(() => {
    setSelectedNoteIds(prev =>
      prev.length === deletableNoteIds.length && deletableNoteIds.length > 0
        ? []
        : deletableNoteIds,
    );
  }, [deletableNoteIds]);

  const clearSelection = useCallback(() => {
    setSelectedNoteIds([]);
  }, []);

  // Actions
  const deleteSelectedNotes = useCallback(async () => {
    if (selectedNoteIds.length === 0) return { failedCount: 0 };

    // allSettled so one blocked note (e.g. signed in another tab since
    // selection — the API returns 409) doesn't abort the rest of the batch.
    const results = await Promise.allSettled(
      selectedNoteIds.map(noteId => deleteNote({ noteId }).unwrap()),
    );
    const failedCount = results.filter(r => r.status === 'rejected').length;

    setSelectedNoteIds([]);
    // RTK Query tag invalidation automatically refetches the list
    return { failedCount };
  }, [selectedNoteIds, deleteNote]);

  const navigateToNote = useCallback(
    (note: UserNoteTypeII) => {
      if (note.status === 'errored') return;

      noteMetrics?.viewed?.(note.note_id, { source: 'list' });

      const path = getNoteRoute({
        note_id: note.note_id,
        thread_id: (note as { thread_id?: string | null })?.thread_id,
        status: note.status,
      });
      navigate(path, { state: { origin: groupId ? 'group' : 'user' } });
    },
    [navigate, noteMetrics, groupId],
  );

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Computed values
  const totalPages = useMemo(
    () => Math.max(1, data?.total_pages ?? Math.ceil(totalCount / itemsPerPage)),
    [data?.total_pages, totalCount, itemsPerPage],
  );

  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;
  const isEmpty = !isLoading && notes.length === 0;
  const isSearchActive = debouncedSearchTerm.length > 0;
  const isAllSelected =
    deletableNoteIds.length > 0 &&
    selectedNoteIds.length === deletableNoteIds.length;
  const hasFirstNote = userState?.onboardingSteps?.generated_first_note ?? false;

  return {
    // Data
    notes,
    isLoading,
    isLoadingFresh,
    error,
    totalCount,

    // Pagination
    currentPage,
    itemsPerPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    setCurrentPage,
    setItemsPerPage,

    // Filters
    category,
    sortOrder,
    searchTerm,
    noteTitleOnly,
    startDate,
    endDate,
    setCategory,
    setSortOrder,
    setSearchTerm,
    setNoteTitleOnly,
    setStartDate,
    setEndDate,

    // Selection
    selectedNoteIds,
    isAllSelected,
    toggleNoteSelection,
    selectAllNotes,
    clearSelection,

    // Actions
    deleteSelectedNotes,
    navigateToNote,
    refresh,

    // State helpers
    isEmpty,
    isSearchActive,
    hasFirstNote,

    // Feature flags
    hasNoteSigning,
  };
}
